// CrossfadeCarousel.js
import React, { useMemo, useRef, useEffect } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/splide/dist/css/splide.min.css';
import './CrossfadeCarousel.css';
function ratioToHeightRatio(r) {
  const [w, h] = String(r).split('/').map(Number);
  return (h && w) ? h / w : 9/16;
}

export default function CrossfadeCarousel(props) {
  const {
    images, slides,
    aspectRatio = '3/2',
    delay = 7000,
    speed = 2400,
    className = '',
    maxWidth = '1200px',
    innerWidth = '100%',
    arrows = false,
    pagination = true,
  } = props;

  const imageUrls = useMemo(() => {
    const srcs = (images && images.length ? images : slides) || [];
    return srcs.map(s => (typeof s === 'string' ? s : (s && (s.src || s.image)) || '')).filter(Boolean);
  }, [images, slides]);

  const splideRef = useRef(null);
  const shellRef = useRef(null);

  // Refresh Splide when the wrapper first gets a non-zero width (e.g., tab opens)
  useEffect(() => {
    const shell = shellRef.current;
    const splide = splideRef.current && splideRef.current.splide;
    if (!shell || !splide) return;

    let initialized = false;
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      if (entry.contentRect.width > 0 && !initialized) {
        initialized = true;
        requestAnimationFrame(() => splide.refresh());
      }
    });
    ro.observe(shell);
    return () => ro.disconnect();
  }, []);

  if (!imageUrls.length) return null;

  return (
    <div className={className}>
      {/* Immediate height to avoid 0px during init */}
      <div
        ref={shellRef}
        style={{ width: innerWidth, maxWidth, margin: '0 auto', aspectRatio }}
      >
        <Splide
          className="xfade-splide"  
          ref={splideRef}
          options={{
            type: 'fade',
            rewind: true,
            autoplay: true,
            interval: delay,
            speed,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            pauseOnHover: false,
            pauseOnFocus: false,
            arrows,
            pagination,
            cover: true,
            heightRatio: ratioToHeightRatio(aspectRatio),
            updateOnMove: true,
          }}
          onMounted={(splide) => {
            const ap = splide && splide.Components && splide.Components.Autoplay;
            if (ap && typeof ap.play === 'function') ap.play();
          }}
          onMoved={(splide) => {
            const ap = splide && splide.Components && splide.Components.Autoplay;
            if (ap && typeof ap.play === 'function') ap.play();
          }}
        >
          {imageUrls.map((src, i) => (
            <SplideSlide key={i}>
              <img
                src={src}
                alt=""
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            </SplideSlide>
          ))}
        </Splide>
      </div>
    </div>
  );
}
